/**
 * useProjectSummary — the Stage 1 → 1.5 Project Summary orchestrator seam.
 *
 * TS-060 T5 slice 1 (DEC-COUNCIL-015): `runProjectSummary` moved VERBATIM
 * from App.jsx (v5.46.76 lines 4041-4166) behind a deps object. The hook
 * owns NO pipeline state — every state value, setter, ref, and helper is
 * threaded in from App.jsx per the useQuoteEditor pattern (state stays in
 * App; a hook that owns a second copy of pipeline state is a defect).
 *
 * Identity contract: the returned `runProjectSummary` is deliberately
 * re-created every render (NO useCallback), exactly like the original
 * per-render closure in App.jsx. The auto-trigger effects in App.jsx call
 * whatever the current render exposed; memoising here would change which
 * render's state a queued call observes.
 *
 * Deps are destructured once per render from a fresh `deps` object built at
 * the call site, so the function body below closes over the SAME values the
 * original closure did on that render. The body text is unchanged —
 * identifiers resolve via destructuring instead of App-scope bindings.
 *
 * Options (moved with the function):
 *   silent - when true, runs in the background without opening the Job
 *            cockpit's Summary tab. The user keeps seeing whatever they're
 *            currently viewing while the AI computes. Used for the
 *            post-import auto-trigger.
 *   autoAdvanceOnReady - automatic Stage 1 continuation: when the summary
 *            lands and the user is still on Stage 1, select the Job
 *            cockpit's Summary tab once. The switch is a tab, not a page,
 *            so the old window.qm_disable_summary_autonav kill switch is
 *            retired (SPEC 2026-10-21 K8).
 */

import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { classifyAiError } from '../services/ai-error-handling';
import { hasMeaningfulProjectInput } from '../utils/meaningful-input';
import { createSelectedFileScope, fileMatchesSelectedScope } from '../services/selected-file-scope';
import { prepareStage1ProjectIdentity, mergePreparedStage1Identity, mergeStage1AssessorNotes, mergeStage1TextFileNotes } from '../services/stage1-project-identity';
import { completeStage1ImportForm, isFolderImportedDocument } from '../services/stage1-import-completion';
import { resolveQuoteAttention } from '../services/quote-attention';
import {
    contentProvenanceMatches,
    createTextProvenance,
} from '../utils/source-provenance';
import { rebindVerifiedRfqAuthority } from '../utils/rfq-authority-binding';
import { isAttendedHandoffPage } from '../utils/page-routing';
import {
    captureProjectRunContext,
    createStaleProjectRunError,
    isProjectRunContextCurrent,
} from '../utils/project-run-context';
import {
    buildProjectSummaryEvidenceFingerprint,
    selectProjectSummaryPhotos,
} from '../utils/project-summary-evidence';

/** Minimal structural view of App's formData — only the fields this seam touches. */
interface SummaryFormData {
    notes?: string;
    address?: string;
    jobDescription?: string;
    /**
     * Content-bound provenance for jobDescription. Consumers must only apply
     * this marker while its fingerprint (or legacy value) still matches the
     * field; an operator edit then supersedes the AI value without every input
     * surface needing to clear it.
     */
    jobDescriptionProvenance?: {
        source: 'ai-project-summary' | 'intake-user' | 'intake-client' | 'intake-operator';
        value: string;
        version?: 1;
        contentFingerprint?: string;
    };
    [key: string]: unknown;
}

const EXPLICIT_INTAKE_PROVENANCE = new Set([
    'intake-user',
    'intake-client',
    'intake-operator',
]);

// The receipt minting moved VERBATIM to utils/rfq-authority-binding so the
// source-bound recovery mints exactly what this save mints.
const bindVerifiedRfqAuthority = rebindVerifiedRfqAuthority;

function bindProjectSummaryFocus(
    formData: SummaryFormData,
    nextScopeObjective: string,
    previousAutoFocus: string,
    lastAutoProjectFocusRef: MutableRefObject<string>,
): SummaryFormData {
    if (!nextScopeObjective) return formData;
    const currentFocus = String(formData.jobDescription || '').trim();
    const provenance = formData.jobDescriptionProvenance;
    const provenanceSource = String(provenance?.source || '');
    const markerApplies = contentProvenanceMatches(provenance, currentFocus);
    const hasExplicitIntakeProvenance = markerApplies
        && EXPLICIT_INTAKE_PROVENANCE.has(provenanceSource);
    if (currentFocus && hasExplicitIntakeProvenance) return formData;

    const hasCurrentAiMarker = markerApplies && provenanceSource === 'ai-project-summary';
    const isPriorAutoFocus = !!currentFocus && currentFocus === previousAutoFocus;
    const matchesFreshAiObjective = currentFocus === nextScopeObjective;
    if (currentFocus && !hasCurrentAiMarker && !isPriorAutoFocus && !matchesFreshAiObjective) {
        // A content edit invalidates the old marker and therefore wins over AI.
        return formData;
    }

    lastAutoProjectFocusRef.current = nextScopeObjective;
    const nextProvenance = createTextProvenance('ai-project-summary', nextScopeObjective);
    if (
        matchesFreshAiObjective
        && provenanceSource === nextProvenance.source
        && contentProvenanceMatches(provenance, nextScopeObjective)
    ) {
        return formData;
    }
    return {
        ...formData,
        jobDescription: nextScopeObjective,
        jobDescriptionProvenance: nextProvenance as SummaryFormData['jobDescriptionProvenance'],
    };
}

/** AI-prompt-contracted summary blob (OPAQUE-style: inner shape owned by the prompt). */
interface ProjectSummaryLike {
    scope_objective?: unknown;
    scopeObjective?: unknown;
    [key: string]: unknown;
}

interface ProjectSummaryFailureReceipt {
    fingerprint: string;
    failedAt: string;
    message: string;
    userMessage: string;
    code: string | null;
    category: string | null;
    statusCode: number | null;
    retryable: boolean;
    operatorAction: string | null;
    retryAfterSeconds: number | null;
    details: unknown;
}

/**
 * The last Summary this project published, and the evidence its run was asked
 * to summarise. The failure latch above only stops a FAILED attempt repeating;
 * this stops a successful one. If a saved Summary reads as stale for any reason
 * the run itself cannot change (the 2026-10-21 loop was a lane the receipt and
 * the screen fingerprinted from different sources), every automatic trigger
 * would otherwise pay for the same Summary again.
 */
export interface ProjectSummarySuccessReceipt {
    projectId: string;
    /** The evidence on screen when the run that produced it started. */
    requestedEvidenceFingerprint: string;
    /** The evidence the saved Summary records it was built from. */
    savedEvidenceFingerprint: string;
    /**
     * Set once the screen has shown this Summary fresh (its evidence on screen).
     * After that, a return to the run's starting evidence is the operator's own
     * change (for example clearing a field the run filled), not the run's, so it
     * no longer counts as "nothing new".
     */
    screenShowedSaved?: boolean;
}

/**
 * An automatic Summary run the success latch refused while the Summary on screen
 * was out of date. Stage 1 says so, with a way forward, for as long as this
 * project, this evidence and this Summary are what is on screen.
 */
export interface ProjectSummaryRerunHold {
    projectId: string;
    /** The evidence on screen when the run was refused. */
    evidenceFingerprint: string;
    /** The evidence the Summary on screen was made from. */
    summaryEvidenceFingerprint: string;
}

/** App's freshness effect: record that the screen has shown the latest published Summary fresh. */
export function markProjectSummarySuccessSeenFresh(
    successRef: MutableRefObject<ProjectSummarySuccessReceipt | null> | null | undefined,
    { projectId, evidenceFingerprint }: { projectId: string | null | undefined; evidenceFingerprint: string },
): void {
    const receipt = successRef?.current;
    if (!receipt || receipt.screenShowedSaved) return;
    if (receipt.projectId !== String(projectId ?? '')) return;
    if (!evidenceFingerprint || evidenceFingerprint !== receipt.savedEvidenceFingerprint) return;
    receipt.screenShowedSaved = true;
}

/** Whether a refused automatic run still describes the screen: same project, same evidence, same Summary. */
export function projectSummaryRerunHoldIsCurrent(
    hold: ProjectSummaryRerunHold | null | undefined,
    { projectId, evidenceFingerprint, summaryEvidenceFingerprint }: {
        projectId: string | null | undefined;
        evidenceFingerprint: string;
        summaryEvidenceFingerprint: string;
    },
): boolean {
    return !!hold
        && hold.projectId === String(projectId ?? '')
        && hold.evidenceFingerprint === evidenceFingerprint
        && hold.summaryEvidenceFingerprint === summaryEvidenceFingerprint;
}

interface ProjectSummaryInputOverride {
    documents?: unknown[];
    photos?: unknown[];
    videoTranscripts?: unknown[];
    preprocessedFiles?: unknown[];
}

interface RunProjectSummaryOptions {
    batchMode?: boolean;
    silent?: boolean;
    autoAdvanceOnReady?: boolean;
    automatic?: boolean;
    /**
     * Full AI's one retry of a failed Summary (SPEC 2026-10-21 H2): an automatic
     * run that may repeat the attempt that just failed, once. App allows one per
     * evidence fingerprint; the latch below still refuses every other repeat.
     */
    retryAfterFailure?: boolean;
    summaryInputOverride?: ProjectSummaryInputOverride | null;
    selectedFileIds?: string[] | null;
    onSummarySaved?: (result: { summaryEvidenceFingerprint: string }) => boolean | void;
    /**
     * Called after this run has written the form itself (filled RFQ identity,
     * appended assessor notes) with the same pure transform it applied to the
     * live form. An attended caller applies it to ITS OWN copy of the form, so
     * the run's enrichment is told apart from an operator edit that lands while
     * the run works, rather than being read back from the live form.
     */
    onOwnedFormChange?: (apply: (form: Record<string, unknown>) => Record<string, unknown>) => void;
    /**
     * Asked only when a SILENT run fails terminally: is the operator still where
     * this run was started for? It publishes nothing and grants nothing (unlike
     * `onSummarySaved`, which can grant Risk). Absent means yes, which keeps the
     * behaviour of callers that have no navigation intent to consult.
     */
    isViewIntentCurrent?: () => boolean;
}

function _summaryFingerprintHash(value: string): string {
    let hash = 0x811c9dc5;
    for (let i = 0; i < value.length; i += 1) {
        hash ^= value.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash.toString(16).padStart(8, '0');
}

/**
 * Latch key that avoids storing API keys or whole documents. Any meaningful
 * input/model/key change produces a fresh key; manual runs bypass the latch.
 */
export function buildProjectSummaryAttemptFingerprint({
    apiKey,
    model,
    currentProjectId,
    projectSummaryRecoveryRevision,
    formData,
    documents,
    photos,
    videoTranscripts,
}: Pick<UseProjectSummaryDeps,
    'apiKey' | 'model' | 'currentProjectId' | 'projectSummaryRecoveryRevision' | 'formData' | 'documents' | 'photos' |
    'videoTranscripts'>): string {
    const keyFingerprint = apiKey ? _summaryFingerprintHash(String(apiKey)) : 'none';
    const evidenceFingerprint = buildProjectSummaryEvidenceFingerprint({
        formData,
        documents,
        photos,
        videoTranscripts,
    });
    const payload = JSON.stringify({
        keyFingerprint,
        model: String(model || ''),
        projectId: String(currentProjectId || 'new-project'),
        providerConfigurationRevision: Number(projectSummaryRecoveryRevision || 0),
        evidenceFingerprint,
    });
    return `project-summary-v1:${_summaryFingerprintHash(payload)}`;
}

function _projectSummaryFailureReceipt(err: unknown, fingerprint: string): ProjectSummaryFailureReceipt {
    const source = err && typeof err === 'object'
        ? err as Record<string, unknown>
        : { message: String(err || 'Project summary failed') };
    const aiError = classifyAiError(source);
    const rawMessage = String(source.message || source.error || 'Failed to generate project summary');
    const statusCode = Number(source.statusCode || source.status || 0) || null;
    const explicitlyRetryable = typeof source.retryable === 'boolean' ? source.retryable : null;
    const retryable = explicitlyRetryable ?? (aiError.isAiProviderError ? aiError.retryable : true);
    return {
        fingerprint,
        failedAt: new Date().toISOString(),
        message: rawMessage,
        userMessage: aiError.isAiProviderError ? aiError.userMessage : rawMessage,
        code: String(source.code || '').trim() || null,
        category: String(source.category || aiError.kind || '').trim() || null,
        statusCode,
        retryable,
        operatorAction: String(source.operatorAction || aiError.operatorAction || '').trim() || null,
        retryAfterSeconds: Number(source.retryAfterSeconds || aiError.retryAfterSeconds || 0) || null,
        details: source.details || source.aiError || null,
    };
}

function _projectSummaryFailureMessage(failure: ProjectSummaryFailureReceipt): string {
    const action = String(failure.operatorAction || '').trim();
    if (!action || failure.userMessage.toLowerCase().includes(action.toLowerCase())) {
        return failure.userMessage;
    }
    return `${failure.userMessage} ${action}`;
}

interface ShowConfirmOptions {
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string | null;
    confirmColor?: string;
    icon?: string;
    zIndex?: number;
    onConfirm?: () => void;
    onCancel?: () => void;
    onDismiss?: () => void;
}

export interface UseProjectSummaryDeps {
    // ── State values (fresh each render, same as the original closure) ──
    apiKey: string | null | undefined;
    model: string;
    currentProjectId: string | null | undefined;
    projectSummaryRecoveryRevision: number;
    formData: SummaryFormData;
    formDataRef: MutableRefObject<SummaryFormData>;
    documents: unknown[];
    photos: unknown[];
    videos?: unknown[];
    videoTranscripts: unknown[];
    preprocessedFiles: unknown[];
    projectSummary: ProjectSummaryLike | null;
    // ── React state setters (App-owned; guaranteed stable) — except
    // setSettingsOpen, which is App's legacy navigate-shim (App.jsx:1035),
    // re-created per render like the original closure expected. ──
    setSettingsOpen: (open: boolean) => void;
    setIsSummarizing: Dispatch<SetStateAction<boolean>>;
    setSummaryError: Dispatch<SetStateAction<string | null>>;
    /**
     * The out-of-date notice (optional so older test harnesses keep working).
     * Never summaryError: the Summary tab renders that as a failure, which
     * would hide a usable Summary and send NEXT to remake it.
     */
    setSummaryRerunHold?: Dispatch<SetStateAction<ProjectSummaryRerunHold | null>>;
    /**
     * One-shot request for a Job cockpit tab on Stage 1 (optional so older test
     * harnesses keep working). Replaces the old switch to the Stage 1.5 page.
     */
    requestCockpitTab?: (tab: 'summary' | 'files') => void;
    /**
     * Told when a run's summary is saved (optional). The Summary tab speaks
     * only for a summary that landed in this session, never on project open.
     */
    onSummaryLanded?: (summary: ProjectSummaryLike) => void;
    /**
     * Told when an edit stopped a run that still owns the project (a stale-run error while it prepared its evidence or saved
     * its writes), so Stage 1 can say why it waits (K11). Optional. The 2 s watcher remakes the summary once typing stops.
     */
    onSummaryRunInterrupted?: () => void;
    setProjectSummary: Dispatch<SetStateAction<ProjectSummaryLike | null>>;
    setSummaryNeedsRefresh: Dispatch<SetStateAction<boolean>>;
    setLastSummarizedSnapshot: Dispatch<SetStateAction<{
        notes: string | undefined;
        address: string | undefined;
        photoCount: number;
        docCount: number;
        evidenceFingerprint: string;
    } | null>>;
    setFormData: Dispatch<SetStateAction<SummaryFormData>>;
    // ── Live-mirror refs (App-owned; read after awaits per the original) ──
    isSummarizingRef: MutableRefObject<boolean>;
    projectSummaryGenerationOwnerRef: MutableRefObject<number>;
    inspectorNotesAiPausedRef: MutableRefObject<boolean>;
    projectAbortRef: MutableRefObject<{ signal: AbortSignal }>;
    currentProjectIdRef: MutableRefObject<string | null>;
    projectLoadGenerationRef: MutableRefObject<number>;
    currentPageRef: MutableRefObject<string>;
    stageRef: MutableRefObject<number>;
    isLoadingProjectRef: MutableRefObject<boolean>;
    lastAutoProjectFocusRef: MutableRefObject<string>;
    projectSummaryFailureRef: MutableRefObject<ProjectSummaryFailureReceipt | null>;
    /**
     * Success latch (optional so older test harnesses keep working). An
     * automatic run refuses while the Summary on screen is the one this receipt
     * records and the evidence is what that run started from or produced. A
     * manual run always runs and replaces the receipt.
     */
    projectSummarySuccessRef?: MutableRefObject<ProjectSummarySuccessReceipt | null>;
    /**
     * STOP-button halt latch (optional so older test harnesses keep working).
     * Automatic runs refuse while set; a manual run clears it.
     */
    aiPipelineHaltedRef?: MutableRefObject<boolean>;
    /**
     * Review mode: set when a project load completes, cleared by any deliberate
     * act. An AUTO run refuses while it is set; a MANUAL run clears it. Same
     * shape as the STOP latch above, different meaning — "the operator has not
     * asked for this" rather than "the operator asked me to stop".
     */
    openedForReviewRef?: MutableRefObject<boolean>;
    // ── App-owned helpers ──
    showConfirm: (options: ShowConfirmOptions) => void;
    clearInspectorNotesAiPause: () => void;
    resetProjectSummaryFocusRecoveryAuthority: () => void;
    saveCurrentProject: (
        overrides?: Record<string, unknown>,
        options?: { runContext?: ReturnType<typeof captureProjectRunContext>; beforeCommit?: () => void },
    ) => Promise<unknown>;
    isPipelineAbort: (err: unknown) => boolean;
    prepareStage1Evidence?: (options: { expectedProjectId: string | null; selectedFileIds: string[] | null; signal: AbortSignal; setAsideUnreadableFiles?: boolean }) => Promise<{ project: any }>;
}

export function useProjectSummary(deps: UseProjectSummaryDeps) {
    const {
        apiKey,
        model,
        currentProjectId,
        projectSummaryRecoveryRevision,
        formData,
        formDataRef,
        documents,
        photos,
        videos = [],
        videoTranscripts,
        preprocessedFiles,
        projectSummary,
        setSettingsOpen,
        setIsSummarizing,
        setSummaryError,
        setSummaryRerunHold,
        requestCockpitTab,
        onSummaryLanded,
        onSummaryRunInterrupted,
        setProjectSummary,
        setSummaryNeedsRefresh,
        setLastSummarizedSnapshot,
        setFormData,
        isSummarizingRef,
        projectSummaryGenerationOwnerRef,
        inspectorNotesAiPausedRef,
        projectAbortRef,
        currentProjectIdRef,
        projectLoadGenerationRef,
        currentPageRef,
        stageRef,
        isLoadingProjectRef,
        lastAutoProjectFocusRef,
        projectSummaryFailureRef,
        projectSummarySuccessRef,
        aiPipelineHaltedRef,
        openedForReviewRef,
        showConfirm,
        clearInspectorNotesAiPause,
        resetProjectSummaryFocusRecoveryAuthority,
        saveCurrentProject,
        isPipelineAbort,
        prepareStage1Evidence,
    } = deps;

    const runProjectSummary = async ({
        batchMode = false,
        silent = false,
        autoAdvanceOnReady = false,
        automatic = false,
        retryAfterFailure = false,
        summaryInputOverride = null,
        selectedFileIds = null,
        onSummarySaved,
        onOwnedFormChange,
        isViewIntentCurrent,
    }: RunProjectSummaryOptions = {}) => {
        const summaryRunContext = captureProjectRunContext({
            projectId: currentProjectIdRef.current ?? currentProjectId ?? null,
            loadGeneration: projectLoadGenerationRef.current,
            signal: projectAbortRef.current.signal,
        });
        let summaryOwner: number | null = null;
        const summaryRunOwnsWriteback = () => isProjectRunContextCurrent(summaryRunContext, {
            projectId: currentProjectIdRef.current,
            loadGeneration: projectLoadGenerationRef.current,
            signal: projectAbortRef.current.signal,
        }) && (summaryOwner === null || projectSummaryGenerationOwnerRef.current === summaryOwner);
        const assertSummaryRunOwnsWriteback = () => {
            if (!summaryRunOwnsWriteback()) throw createStaleProjectRunError('Project Summary');
        };
        if (!summaryRunOwnsWriteback()) return;
        // Ref-based concurrency guard - if another Project Summary run is
        // already in flight, no-op. Prevents duplicate fires when two auto-
        // trigger effects (file-import based + manual-entry based) both
        // clear their isSummarizing state check and race into this function
        // before React has flushed the setIsSummarizing(true) from the first.
        if (isSummarizingRef.current) {
            console.log('[QM] runProjectSummary: another run already in flight, skipping');
            return;
        }
        // ─── STOP-button halt latch (2026-07-28) ─────────────────────────────
        // After a user STOP, the auto-trigger effects must not quietly restart
        // the cascade from the top. A manual run (summary button / "Continue"
        // in the inspector-notes dialog) clears the latch and re-opens it.
        if (automatic && aiPipelineHaltedRef?.current) {
            console.info('[QM] runProjectSummary: automatic run suppressed — pipeline halted by STOP');
            return;
        }
        if (automatic && openedForReviewRef?.current) {
            console.info('[QM] runProjectSummary: automatic run suppressed — project opened for review');
            return;
        }
        if (!automatic && aiPipelineHaltedRef) {
            aiPipelineHaltedRef.current = false;
        }
        if (!automatic && openedForReviewRef) {
            openedForReviewRef.current = false;
        }
        if (!automatic) {
            // Explicit user retry is the only non-material action that may
            // re-arm the single browser-focus recovery for this exact failure
            // family. Do this before the hook clears the failure receipt below.
            resetProjectSummaryFocusRecoveryAuthority();
        }
        // Pin THIS run to the controller live at run start (STOP re-arms a
        // fresh controller; a late read would detach this run from the STOP).
        const runAbortSignal = summaryRunContext.signal as AbortSignal;
        let inputDocuments = Array.isArray(summaryInputOverride?.documents)
            ? summaryInputOverride.documents
            : documents;
        let inputPhotos = Array.isArray(summaryInputOverride?.photos)
            ? summaryInputOverride.photos
            : photos;
        let inputVideoTranscripts = Array.isArray(summaryInputOverride?.videoTranscripts)
            ? summaryInputOverride.videoTranscripts
            : videoTranscripts;
        let inputPreprocessedFiles = Array.isArray(summaryInputOverride?.preprocessedFiles)
            ? summaryInputOverride.preprocessedFiles
            : preprocessedFiles;
        let summaryPhotos = selectProjectSummaryPhotos(inputPhotos);
        let attemptFingerprint = buildProjectSummaryAttemptFingerprint({
            apiKey,
            model,
            currentProjectId,
            projectSummaryRecoveryRevision,
            formData,
            documents: inputDocuments,
            photos: summaryPhotos,
            videoTranscripts: inputVideoTranscripts,
        });
        // The evidence this run is asked to summarise, as the screen fingerprints it.
        const requestedEvidenceFingerprint = buildProjectSummaryEvidenceFingerprint({
            formData,
            documents: inputDocuments,
            photos: summaryPhotos,
            videoTranscripts: inputVideoTranscripts,
        });
        const priorFailure = projectSummaryFailureRef.current;
        // `retryable` is the REASON shown to the operator, never the gate.
        //
        // It used to be part of this condition, which made the suppression a
        // no-op for exactly the failures that repeat: the App.jsx:2396 effect
        // re-arms a 2000ms timer whenever `isSummarizing` flips back and
        // `projectSummary` is still null (which a failure guarantees), so a
        // failure classified retryable — or not classified at all, e.g. a bare
        // `Error('Failed to fetch')` from a dropped connection — fired a PAID
        // call every two seconds, indefinitely, with the operator seeing only
        // a silent spinner. One network blip became an unbounded spend.
        //
        // The rule now matches the latch Stage 2 already uses: an AUTOMATIC
        // attempt whose inputs are byte-identical to one that just failed is
        // refused whatever the reason, because repeating an identical request
        // cannot produce a different answer. Every operator-driven route out
        // stays open — a manual Try Again passes `automatic: false`, and a real
        // input change or a recovery-revision bump changes the fingerprint,
        // both of which clear the latch at :436.
        if (automatic && !retryAfterFailure && priorFailure?.fingerprint === attemptFingerprint) {
            console.info('[QM] Project summary automatic retry suppressed until its input/config changes or the operator retries.', {
                code: priorFailure.code,
                category: priorFailure.category,
            });
            // A different state transition may have cleared the visible error
            // while leaving the terminal receipt latched. Re-assert the full
            // actionable message whenever an automatic duplicate is refused.
            setSummaryError(_projectSummaryFailureMessage(priorFailure));
            return;
        }
        // The success latch. While the Summary on screen is the one the last run
        // published, and the evidence is what that run started from or what it
        // saved, an automatic run can only pay for the same answer again. So a
        // successful Summary is re-run unattended at most once without new
        // evidence: the run that its own writes (identity, notes) moved on to.
        // Changed evidence, a different Summary on screen, another project or a
        // manual run (`automatic: false`) all pass. So does a return to the
        // run's starting evidence after the screen showed its Summary fresh:
        // that is the operator undoing the run's writes, and the Summary on
        // screen now describes evidence that is gone.
        const priorSuccess = projectSummarySuccessRef?.current;
        const summaryOnScreen = String(
            projectSummary?.summaryEvidenceFingerprint
            || projectSummary?.summary_evidence_fingerprint
            || '',
        );
        if (
            automatic
            && priorSuccess
            && priorSuccess.projectId === String(currentProjectIdRef.current ?? currentProjectId ?? '')
            && summaryOnScreen === priorSuccess.savedEvidenceFingerprint
            && ((requestedEvidenceFingerprint === priorSuccess.requestedEvidenceFingerprint && !priorSuccess.screenShowedSaved)
                || requestedEvidenceFingerprint === priorSuccess.savedEvidenceFingerprint)
        ) {
            console.warn('[QM] Project summary automatic re-run refused: the Summary on screen was made from this evidence. It runs again when the evidence changes or the operator regenerates it.');
            // A fresh Summary has nothing to say. An out-of-date one would leave
            // Stage 1 saying "Summary ready", so the hold makes Stage 1 say
            // "Summary out of date" and offer Refresh in the Summary tab, and
            // Full AI goes on with it as it is (App, SPEC 2026-10-21 H2).
            if (requestedEvidenceFingerprint !== summaryOnScreen) {
                const hold: ProjectSummaryRerunHold = {
                    projectId: priorSuccess.projectId,
                    evidenceFingerprint: requestedEvidenceFingerprint,
                    summaryEvidenceFingerprint: summaryOnScreen,
                };
                setSummaryRerunHold?.((prev) => (
                    prev
                    && prev.projectId === hold.projectId
                    && prev.evidenceFingerprint === hold.evidenceFingerprint
                    && prev.summaryEvidenceFingerprint === hold.summaryEvidenceFingerprint
                        ? prev
                        : hold
                ));
            }
            return;
        }
        if (!automatic || (priorFailure && priorFailure.fingerprint !== attemptFingerprint)) {
            projectSummaryFailureRef.current = null;
        }
        if (!apiKey) {
            if (!silent) setSettingsOpen(true);
            return;
        }
        if (inspectorNotesAiPausedRef.current) {
            if (!silent) {
                showConfirm({
                    title: 'Inspector Notes Still Active',
                    message: 'Do you want to continue or do you need more time to edit the inspector notes?',
                    confirmText: 'Continue',
                    cancelText: 'I Need More Time',
                    confirmColor: 'orange',
                    icon: 'info',
                    onConfirm: () => {
                        clearInspectorNotesAiPause();
                        window.setTimeout(() => runProjectSummary({ silent, autoAdvanceOnReady, summaryInputOverride, selectedFileIds, onSummarySaved, onOwnedFormChange, isViewIntentCurrent }), 0);
                    },
                    onCancel: () => {},
                });
            }
            return;
        }
        // The one "is there anything to summarise" rule (O-S9, C03): visibly empty notes (whitespace, `<p><br></p>`) count as none, and
        // a retained video counts as evidence whether or not it could be transcribed.
        if (!hasMeaningfulProjectInput({
            notes: formData.notes,
            documentCount: inputDocuments.length,
            photoCount: summaryPhotos.length,
            transcriptCount: inputVideoTranscripts.length,
            videoCount: videos.length,
        })) {
            if (!silent) showConfirm({ title: 'Missing Information', message: 'Please add RFQ documents, photos, or notes before continuing.', confirmText: 'Got it', cancelText: null, confirmColor: 'orange', icon: 'info', onConfirm: () => {} });
            return;
        }

        summaryOwner = ++projectSummaryGenerationOwnerRef.current;
        isSummarizingRef.current = true;
        setIsSummarizing(true);
        setSummaryError(null);
        setSummaryRerunHold?.(null);
        // A run starting shows the Job cockpit's Summary tab, so its progress is
        // on screen (B6). A background run does too while Stage 1 is showing;
        // a Quote Run never touches the screen.
        if (!silent || (!batchMode && currentPageRef.current === 'quotemaker' && Number(stageRef.current) === 1)) {
            requestCockpitTab?.('summary');
        }

        try {
            // A project row and its portable media references must exist before
            // the first paid Stage 1.5 call. Production previously continued
            // after a rejected inline-media save, spent the summary/forensic
            // calls, then failed at a later receipt with no durable owner.
            await saveCurrentProject({}, { runContext: summaryRunContext });
            assertSummaryRunOwnsWriteback();
            if (prepareStage1Evidence) {
                const prepared = await prepareStage1Evidence({
                    expectedProjectId: currentProjectIdRef.current ?? currentProjectId ?? null,
                    selectedFileIds, signal: runAbortSignal,
                    // A file QuoteMaker cannot read is set aside and the run
                    // continues (Peter, 2026-10-22), in a Quote Run too
                    // (2026-10-23: it carries on and flags as Full AI does).
                    setAsideUnreadableFiles: true,
                });
                assertSummaryRunOwnsWriteback();
                const selection = createSelectedFileScope(selectedFileIds);
                const selected = (items: unknown) => (Array.isArray(items) ? items : [])
                    .filter(item => !selection || fileMatchesSelectedScope(item, selection));
                inputDocuments = selected(prepared.project.documents);
                inputPhotos = prepared.project.photos || [];
                inputVideoTranscripts = selected(prepared.project.videoTranscripts);
                inputPreprocessedFiles = selected(prepared.project.preprocessedFiles);
                summaryPhotos = selectProjectSummaryPhotos(inputPhotos);
                attemptFingerprint = buildProjectSummaryAttemptFingerprint({
                    apiKey, model, currentProjectId: currentProjectIdRef.current ?? currentProjectId,
                    projectSummaryRecoveryRevision, formData: formDataRef.current || formData,
                    documents: inputDocuments, photos: summaryPhotos,
                    videoTranscripts: inputVideoTranscripts,
                });
            }
            // The RFQ text and the client's own Scope of Works a Stage 1 drop
            // writes when it imports the documents. A folder import (Import
            // Projects, the Quote Run) never wrote them, so the Work Method,
            // the Stage 4 seed and the quote went without both (Peter,
            // 2026-10-23: the routes must behave the same). A field that already
            // holds a value is kept, so an RFQ the operator chose stays, and a
            // job built in Stage 1 is never touched here.
            const importBase = formDataRef.current || formData;
            const importForm = (inputDocuments.some(isFolderImportedDocument)
                ? completeStage1ImportForm(importBase, inputDocuments)
                : importBase) as SummaryFormData;
            if (importForm !== importBase) {
                const beforeCommit = () => {
                    assertSummaryRunOwnsWriteback();
                    if (formDataRef.current !== importBase) throw createStaleProjectRunError('RFQ text changed during save');
                };
                await saveCurrentProject({ formData: importForm }, { runContext: summaryRunContext, beforeCommit });
                beforeCommit();
                formDataRef.current = importForm;
                setFormData(importForm);
                onOwnedFormChange?.((form) => completeStage1ImportForm(form, inputDocuments));
            }
            // Raw folder imports arrive with durable evidence but no attended
            // document-import callback. Complete the same additive Stage 1
            // contact extraction here for BOTH entry paths before Summary.
            const preparedIdentity = await prepareStage1ProjectIdentity({
                formData: formDataRef.current || formData,
                documents: inputDocuments,
                apiKey,
                model,
                projectId: currentProjectIdRef.current ?? currentProjectId ?? null,
                signal: runAbortSignal,
                // A folder import never had the Stage 1 read of the request.
                readEvenWhenComplete: inputDocuments.some(isFolderImportedDocument),
            });
            assertSummaryRunOwnsWriteback();
            if (preparedIdentity) {
                const identityBase = formDataRef.current || formData;
                const identityForm = mergePreparedStage1Identity(identityBase, preparedIdentity) as SummaryFormData;
                await saveCurrentProject({ formData: identityForm }, { runContext: summaryRunContext });
                assertSummaryRunOwnsWriteback();
                // Never replace an operator edit made during the persistence
                // await. Re-entering Summary will rebase against their edit.
                if (formDataRef.current !== identityBase) {
                    throw createStaleProjectRunError('RFQ identity changed during save');
                }
                formDataRef.current = identityForm;
                setFormData(identityForm);
                onOwnedFormChange?.((form) => mergePreparedStage1Identity(form, preparedIdentity));
            }
            // The assessor's own site notes belong in Inspector Notes, where the
            // Summary, Risk and Work Method prompts all read them. The attended
            // import screen places them itself; a queued import never mounts
            // that screen, so its projects reached the AI with the notes field
            // holding only a video transcript. Deterministic, no provider call,
            // append only, and saved through the ordinary path so the browser
            // and the server never disagree about a builder input.
            // A folder import's plain text notes go in first, as a Stage 1 drop
            // puts every such file in the notes (Peter, 2026-10-23).
            const folderDocuments = inputDocuments.filter(isFolderImportedDocument);
            const withImportedNotes = (form: Record<string, unknown>) => mergeStage1AssessorNotes(
                folderDocuments.length > 0 ? mergeStage1TextFileNotes(form, folderDocuments) : form,
                inputDocuments,
            );
            const notesBase = formDataRef.current || formData;
            const notesForm = withImportedNotes(notesBase) as SummaryFormData;
            if (notesForm !== notesBase) {
                const beforeCommit = () => {
                    assertSummaryRunOwnsWriteback();
                    if (formDataRef.current !== notesBase) throw createStaleProjectRunError('Inspector notes changed during save');
                };
                await saveCurrentProject({ formData: notesForm }, { runContext: summaryRunContext, beforeCommit });
                beforeCommit();
                formDataRef.current = notesForm;
                setFormData(notesForm);
                onOwnedFormChange?.(withImportedNotes);
            }
            // Set deterministic RFQ attention before any Summary/Risk/WMS
            // provider sees the form. The delayed CRM enrichment may not own
            // fields while a pipeline is running.
            const attentionBase = formDataRef.current || formData;
            const manualAttention = String(attentionBase.attentionTo || '').trim()
                && attentionBase.attentionToSource === 'manual';
            const attention = manualAttention ? null : resolveQuoteAttention(
                bindVerifiedRfqAuthority(attentionBase, inputDocuments, inputPreprocessedFiles),
            );
            if (attention?.name && (attentionBase.attentionTo !== attention.name || attentionBase.attentionToSource !== attention.source)) {
                const attentionForm = { ...attentionBase, attentionTo: attention.name, attentionToSource: attention.source };
                const beforeCommit = () => {
                    assertSummaryRunOwnsWriteback();
                    if (formDataRef.current !== attentionBase) throw createStaleProjectRunError('Quote attention changed during save');
                };
                await saveCurrentProject({ formData: attentionForm }, { runContext: summaryRunContext, beforeCommit });
                beforeCommit();
                formDataRef.current = attentionForm;
                setFormData(attentionForm);
            }
            const summaryFormData = formDataRef.current || formData;
            const summaryEvidenceFingerprint = buildProjectSummaryEvidenceFingerprint({
                formData: summaryFormData,
                documents: inputDocuments,
                photos: summaryPhotos,
                videoTranscripts: inputVideoTranscripts,
            });
            // If identity was saved before a downstream failure, the failure
            // latch must describe the enriched inputs now visible in Stage 1.
            attemptFingerprint = buildProjectSummaryAttemptFingerprint({
                apiKey, model, currentProjectId: currentProjectIdRef.current ?? currentProjectId, projectSummaryRecoveryRevision,
                formData: summaryFormData, documents: inputDocuments, photos: summaryPhotos,
                videoTranscripts: inputVideoTranscripts,
            });
            const { generateProjectSummary } = await import('../services/project-summary-service');
            // JSDoc in the .js service types the return as bare `Object`;
            // assert the AI-contracted structural shape (annotation-only).
            const generatedSummary = (await generateProjectSummary({
                apiKey,
                model,
                projectId: currentProjectIdRef.current ?? currentProjectId,
                formData: summaryFormData,
                documents: inputDocuments,
                photos: summaryPhotos,
                videoTranscripts: inputVideoTranscripts,
                // Empty on purpose: the receipt above does not cover this lane
                // (project-summary-evidence.ts), so the prompt must not read it.
                preprocessedFiles: [],
                signal: runAbortSignal,
            })) as ProjectSummaryLike;
            assertSummaryRunOwnsWriteback();
            const summary = {
                ...generatedSummary,
                summaryEvidenceFingerprint,
            } as ProjectSummaryLike;
            projectSummaryFailureRef.current = null;
            // Auto-set the project focus from Stage 1.5's scope objective and
            // bind imported RFQ authority to its classified source document.
            // formDataRef is the live operator-owned value after the AI await;
            // persist the exact derived object before committing UI readiness.
            const nextScopeObjective = String(summary?.scope_objective || '').trim();
            const previousAutoFocus = String(
                projectSummary?.scope_objective
                || projectSummary?.scopeObjective
                || lastAutoProjectFocusRef.current
                || ''
            ).trim();
            const latestFormData = formDataRef.current || formData;
            const withVerifiedRfq = bindVerifiedRfqAuthority(
                latestFormData,
                inputDocuments,
                inputPreprocessedFiles,
            );
            const nextFormData = bindProjectSummaryFocus(
                withVerifiedRfq,
                nextScopeObjective,
                previousAutoFocus,
                lastAutoProjectFocusRef,
            );
            await saveCurrentProject({
                projectSummary: summary,
                formData: nextFormData,
            }, { runContext: summaryRunContext });
            assertSummaryRunOwnsWriteback();

            // nextFormData was derived before the save above. A field the
            // operator edited while that save was in flight (the Client, the
            // address; notes pause the run instead) is newer than it, and
            // replacing the form would silently discard the edit. Keep their
            // form: the Summary then reads as stale against it, exactly as for
            // an edit made during the provider call.
            if ((formDataRef.current || formData) === latestFormData) {
                formDataRef.current = nextFormData;
                setFormData(nextFormData);
            }
            // The receipt goes in before the Summary is published, so App's
            // freshness effect for that render already sees it.
            if (projectSummarySuccessRef) {
                projectSummarySuccessRef.current = {
                    projectId: String(currentProjectIdRef.current ?? currentProjectId ?? ''),
                    requestedEvidenceFingerprint,
                    savedEvidenceFingerprint: summaryEvidenceFingerprint,
                };
            }
            setProjectSummary(summary);
            setSummaryNeedsRefresh(false);
            setLastSummarizedSnapshot({
                notes: latestFormData.notes,
                address: latestFormData.address,
                photoCount: summaryPhotos.length,
                docCount: inputDocuments.length,
                evidenceFingerprint: summaryEvidenceFingerprint,
            });
            // An express import may request the attended Risk continuation
            // only after evidence preparation, identity extraction and this
            // exact Summary/form pair are durable. Its original input
            // fingerprint can change during that work, so return the saved one.
            const continuationAllowed = onSummarySaved?.({ summaryEvidenceFingerprint }) !== false;
            // The landing is what the Summary tab speaks for (B10). A Quote Run
            // never touches the screen, so its summaries land silently and buy
            // no speech, even though each batch load shows Stage 1.
            if (!batchMode
                && summaryRunOwnsWriteback()
                && currentPageRef.current === 'quotemaker'
                && Number(stageRef.current) === 1) {
                onSummaryLanded?.(summary);
            }

            // Select the Summary tab only after the exact summary/form pair is
            // durable, then re-check the live attended surface and halt/review
            // latches.
            if (
                autoAdvanceOnReady
                && continuationAllowed
                // QuoteMaker, or a passive monitoring page the operator opened to watch
                // (Developer Tools, AI Models): the Summary may paint while they watch,
                // and is there when they return. Any other page is somewhere else.
                && isAttendedHandoffPage(currentPageRef.current)
                && stageRef.current === 1
                && !isLoadingProjectRef.current
                && aiPipelineHaltedRef?.current !== true
                && openedForReviewRef?.current !== true
                && summaryRunOwnsWriteback()
            ) {
                console.log('[QM] Project Summary saved from import — showing the Summary tab');
                requestCockpitTab?.('summary');
            }
        } catch (err) {
            if (isPipelineAbort(err) || (err as { name?: string })?.name === 'AbortError') {
                console.info('[QM] Project summary paused/cancelled:', (err as { message?: string })?.message || (err as { name?: string })?.name || 'aborted');
                if (batchMode) throw err;
                // K11: the job changed under a run that still owns it (STOP, another
                // project or a newer run own nothing). Never silent: Stage 1 says it
                // waits, and the summary is remade once typing stops (B2).
                if ((err as { code?: string })?.code === 'PROJECT_RUN_STALE' && summaryRunOwnsWriteback()) {
                    onSummaryRunInterrupted?.();
                }
                return;
            }
            // Publication authority. An ordinary failure may only reach the
            // screen, the failure latch and the attended view while this run
            // still owns the project it started on. A run for another project,
            // an earlier load session of this one, a cancelled run (STOP re-arms
            // the controller) or one a newer run superseded would otherwise
            // paint its error over the project now on screen, latch that
            // project's retries off, or switch its cockpit to the Summary tab.
            if (!summaryRunOwnsWriteback()) {
                console.info('[QM] Project summary failure discarded: the run no longer owns the active project.', {
                    message: (err as { message?: string })?.message,
                });
                if (batchMode) throw err;
                return;
            }
            console.error('[QM] Project summary failed:', err);
            const failure = _projectSummaryFailureReceipt(err, attemptFingerprint);
            projectSummaryFailureRef.current = failure;
            // Every failure is now visible, because none of them retries itself
            // any more.
            //
            // This condition used to be `!silent || failure.retryable === false`,
            // which let a silent transient failure pass in total silence. That was
            // survivable only because the App.jsx:2396 effect re-fired every two
            // seconds and would eventually succeed — the very loop that made an
            // unclassified failure bill forever. With the automatic latch above
            // now refusing any repeat of an identical attempt, staying quiet here
            // would swap an invisible money loop for an invisible dead end: no
            // summary, no retry, and nothing on screen saying why.
            //
            // So the receipt is always surfaced. `silent` still means what it
            // always meant — do not seize the view — and the stronger
            // view-switching response below stays reserved for terminal failures.
            setSummaryError(_projectSummaryFailureMessage(failure));
            // A terminal failure may open the Summary tab for a SILENT run, whose
            // error state carries the reason and a retry, but only while the
            // operator is still where the run was started for. Back, the Stage 1
            // pill or a menu trip since then chose Data Entry (they advance the
            // attended epoch and do not abort the run), and reopening the Summary
            // over that choice undoes it (C25). Data Entry shows the same reason
            // and a Try again, so nothing is lost by respecting the choice. A
            // non-silent run already put the Summary up when it started, so it
            // has nothing to restore; if the operator left it, it stays left.
            if (failure.retryable === false
                && silent
                && (typeof isViewIntentCurrent !== 'function' || isViewIntentCurrent())) {
                requestCockpitTab?.('summary');
            }
            // Quote Run must retain the actual file/provider/save failure,
            // rather than replacing it with a missing-summary readback error.
            if (batchMode) throw err;
        } finally {
            // Cleanup authority is separate from publication authority. A
            // cancelled run that is still the LATEST run clears its own busy
            // state (so retry works); a run a newer one has superseded must
            // never release the newer run's lock, whatever its outcome.
            if (summaryOwner === null || projectSummaryGenerationOwnerRef.current === summaryOwner) {
                isSummarizingRef.current = false;
                setIsSummarizing(false);
            }
        }
    };

    return { runProjectSummary };
}
